import React, { useEffect, useState } from 'react';
import logo from "./assets/slazzer-preview-euzu3.png";

const messages = ['Setting up', 'Loading store', 'Almost ready'];

export default function SplashScreen() {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    // Lock scroll while splash is showing
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      // Restore scroll when splash unmounts
      document.body.style.overflow = prev || '';
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex(i => (i + 1) % messages.length);
    }, 900);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <style>{`
        @keyframes ucart-spin-ring {
          to { transform: rotate(360deg); }
        }
        @keyframes ucart-pulse-orb {
          0%, 100% { transform: scale(1);   opacity: 0.18; }
          50%       { transform: scale(1.2); opacity: 0.28; }
        }
        @keyframes ucart-fill-bar {
          0%   { width: 0%; }
          60%  { width: 75%; }
          100% { width: 100%; }
        }
        @keyframes ucart-logo-in {
          from { transform: scale(0.6); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes ucart-fade-up {
          from { transform: translateY(10px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes ucart-dot-bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(-5px); }
        }

        .ucart-splash {
          position: fixed;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background:
            radial-gradient(ellipse 80% 60% at 50% 0%,   #064e3b 0%, transparent 70%),
            radial-gradient(ellipse 60% 50% at 80% 100%, #022c22 0%, transparent 60%),
            linear-gradient(160deg, #022c22 0%, #000 50%, #064e3b 100%);
          z-index: 9999;
          transition: opacity 0.5s ease, transform 0.5s ease;
          overflow: hidden;
        }

        .ucart-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
        }
        .ucart-orb-1 {
          width: 300px; height: 300px;
          background: #047857;
          top: -80px; left: -80px;
          opacity: 0.18;
          animation: ucart-pulse-orb 4s ease-in-out infinite;
        }
        .ucart-orb-2 {
          width: 200px; height: 200px;
          background: #10b981;
          bottom: -40px; right: -40px;
          opacity: 0.18;
          animation: ucart-pulse-orb 4s 2s ease-in-out infinite;
        }

        .ucart-logo-wrap {
          position: relative;
          margin-bottom: 36px;
          animation: ucart-logo-in 0.7s cubic-bezier(0.34,1.56,0.64,1) both;
        }
        .ucart-logo-ring {
          position: absolute;
          inset: -10px;
          border-radius: 50%;
          border: 2px solid transparent;
          background: conic-gradient(from 0deg, #34d399, transparent 60%, #10b981, transparent) border-box;
          -webkit-mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: destination-out;
          mask-composite: exclude;
          animation: ucart-spin-ring 2.5s linear infinite;
        }
        .ucart-logo-img {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: block;
          box-shadow: 0 0 30px rgba(52,211,153,0.35);
        }

        .ucart-brand {
          font-size: 2.2rem;
          font-weight: 800;
          letter-spacing: -0.03em;
          color: #f0fdf4;
          margin-bottom: 6px;
          animation: ucart-fade-up 0.6s 0.2s ease both;
        }
        .ucart-brand-accent { color: #34d399; }

        .ucart-tagline {
          font-size: 0.8rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(240,253,244,0.45);
          margin-bottom: 48px;
          animation: ucart-fade-up 0.6s 0.35s ease both;
        }

        .ucart-progress-track {
          width: 160px;
          height: 3px;
          background: rgba(255,255,255,0.1);
          border-radius: 99px;
          overflow: hidden;
          animation: ucart-fade-up 0.6s 0.5s ease both;
        }
        .ucart-progress-fill {
          height: 100%;
          width: 0%;
          background: linear-gradient(90deg, #047857, #34d399);
          border-radius: 99px;
          animation: ucart-fill-bar 2.2s 0.6s cubic-bezier(0.4,0,0.2,1) forwards;
          box-shadow: 0 0 8px #34d399;
        }

        .ucart-status {
          margin-top: 18px;
          font-size: 0.78rem;
          color: rgba(240,253,244,0.4);
          letter-spacing: 0.06em;
          animation: ucart-fade-up 0.6s 0.7s ease both;
          min-height: 1.2em;
          font-family: inherit;
        }

        .ucart-dots {
          display: inline-flex;
          gap: 4px;
          margin-left: 4px;
          vertical-align: middle;
        }
        .ucart-dot {
          width: 4px; height: 4px;
          background: #34d399;
          border-radius: 50%;
          animation: ucart-dot-bounce 1.2s ease-in-out infinite;
        }
        .ucart-dot:nth-child(2) { animation-delay: 0.2s; }
        .ucart-dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>

      <div className="ucart-splash" role="status" aria-label="Loading uCart">
        <div className="ucart-orb ucart-orb-1" />
        <div className="ucart-orb ucart-orb-2" />

        <div className="ucart-logo-wrap">
          <div className="ucart-logo-ring" />
          <img
            className="ucart-logo-img"
            src={logo}
            alt="uCart logo"
            width={72}
            height={72}
          />
        </div>

        <p className="ucart-brand">
          u<span className="ucart-brand-accent">Cart</span>
        </p>
        <p className="ucart-tagline">Campus Marketplace</p>

        <div className="ucart-progress-track">
          <div className="ucart-progress-fill" />
        </div>

        <p className="ucart-status">
          {messages[msgIndex]}
          <span className="ucart-dots">
            <span className="ucart-dot" />
            <span className="ucart-dot" />
            <span className="ucart-dot" />
          </span>
        </p>
      </div>
    </>
  );
}